// hooks/useFocusFirstInput.ts
import { useEffect } from 'react';

type FocusableElement =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement
  | HTMLButtonElement;

export const useFocusFirstInput = (
  sectionKey: string,
  shouldFocus: boolean
) => {
  useEffect(() => {
    if (!shouldFocus) return;

    const timeoutId = setTimeout(() => {
      // Comprehensive selector for all focusable form elements
      const selector = [
        // Standard form controls
        'input:not([type="hidden"]):not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        'button:not([disabled])',
        // Radio buttons and checkboxes
        'input[type="radio"]:not([disabled])',
        'input[type="checkbox"]:not([disabled])',
        // Custom elements that are focusable
        '[type="button"]:not([disabled])',
        '[type="radio"]:not([disabled])',
        '[type="checkbox"]:not([disabled])',
        '[type="combobox"]:not([disabled])',
        '[type="textbox"]:not([disabled])',
        // Elements with tabindex
        '[tabindex]:not([tabindex="-1"])',
      ].join(', ');

      // Find all focusable elements within the section
      const form = document.querySelector(`[data-section="${sectionKey}"]`);

      if (!form) {
        console.warn(`⚠️ Section not found: ${sectionKey}`);
        return;
      }

      // Get all focusable elements and sort by tabindex
      const focusableElements = Array.from(
        form.querySelectorAll<FocusableElement>(selector)
      ).filter((element) => {
        // Ensure element is visible
        const style = window.getComputedStyle(element);
        return (
          style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          style.opacity !== '0'
        );
      });

      // Sort elements by tabindex
      const sortedElements = focusableElements.sort((a, b) => {
        const aTabIndex = a.tabIndex || 0;
        const bTabIndex = b.tabIndex || 0;

        if (aTabIndex === bTabIndex) {
          // If same tabindex, maintain DOM order
          return 0;
        }

        if (aTabIndex === 0) return 1;
        if (bTabIndex === 0) return -1;

        return aTabIndex - bTabIndex;
      });

      const firstFocusableElement = sortedElements[0];

      if (firstFocusableElement) {
        firstFocusableElement.focus();

        // If it's a text input, optionally select the content
        if (
          firstFocusableElement instanceof HTMLInputElement &&
          ['text', 'email', 'number', 'tel', 'url'].includes(
            firstFocusableElement.type
          )
        ) {
          firstFocusableElement.select();
        }

        console.log(`✅ Focused element:`, {
          type: firstFocusableElement.tagName,
          role: firstFocusableElement.getAttribute('role'),
          id: firstFocusableElement.id,
          name: firstFocusableElement.name,
        });
      } else {
        console.warn(
          `⚠️ No focusable elements found in section: ${sectionKey}`
        );
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [sectionKey, shouldFocus]);
};
